import type { Space } from '../../../lib/flotiq-client/types/api-response';

import { useTranslation } from 'react-i18next';

import { getEnv } from '../../../lib/env';
import { getTestProps } from '../../../lib/helpers';

import Button from '../../../components/Button/Button';
import Loader from '../../../components/Loader/Loader';

interface SpaceSaveButtonProps {
  /**
   * Mode of the form
   */
  mode: string;
  /**
   * If the button should be disabled
   */
  disabled?: boolean;
  /**
   * Space to edit.
   */
  space?: Space | null;
  /**
   * Values from the form
   */
  values: {
    planPrice: number;
    paymentInterval?: string;
    planVisibleName?: string;
  };
  /**
   * Test id for the button
   */
  testId?: string;
}

const SpaceSaveButton = ({
  mode,
  disabled,
  space,
  values,
  testId,
}: SpaceSaveButtonProps) => {
  const { t } = useTranslation();

  if (mode === 'edit') return null;

  const isPeriodSame =
    values?.planPrice === 0 && space?.planLimits.price === 0
      ? true
      : (space?.paymentInterval || 'month') === values?.paymentInterval;

  const isPlanSame =
    isPeriodSame && values?.planVisibleName === space?.planLimits?.visible_name;

  let buttonConfig: {
    type: 'submit' | 'button';
    form: string;
    onClick?: () => void;
    text: string;
    disabled?: boolean;
  } = {
    type: 'submit',
    form: 'space-form',
    onClick: undefined,
    text: t('Spaces.Continue'),
    disabled: isPlanSame || disabled,
  };

  const isContactUsButton = values.planPrice === -1;

  if (isContactUsButton) {
    buttonConfig = {
      type: 'button',
      form: '',
      onClick: () =>
        window.open(getEnv('VITE_FLOTIQ_PAGE_CONTACT'), '_blank', 'noopener'),
      text: t('AccountSettings.ContactUs'),
      disabled: disabled,
    };
  }

  return (
    <Button
      type={buttonConfig.type}
      form={buttonConfig.form}
      additionalClasses="mr-auto ml-auto lg:mr-0 mt-3 lg:mt-0"
      iconPosition="start"
      iconImage={
        disabled && (
          <Loader
            type="spinner"
            {...getTestProps(testId, 'save-button-loader', 'testId')}
          />
        )
      }
      disabled={buttonConfig.disabled}
      onClick={buttonConfig.onClick}
      {...getTestProps(testId, 'save-button', 'testId')}
    >
      {buttonConfig.text}
    </Button>
  );
};

export default SpaceSaveButton;
